import { Component, OnDestroy, OnInit } from '@angular/core';
import { ResponsiveService } from '@frk/eds-components';
import { Q1PH, Q3PH } from '@literature/const/literature-data-const';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { LiteratureDocument } from '@types';
import { Logger } from '@utils/logger';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ComplimentaryLiteratureService } from './services/complementary-literature.service';
const logger = Logger.getLogger('ComplimentaryLiteratureComponent');

// Used to mock sections - complimentary, mandatory and share holder material
@Component({
  selector: 'ft-literature-details-thumbnails',
  templateUrl: './complementary-literature.component.html',
})
export class ComplimentaryLiteratureComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  isHandheld$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  titleLinkBase: string;
  complementaryThumbnails$: Observable<LiteratureDocument[]>;
  mandatoryThumbnails$: Observable<LiteratureDocument[]>;
  shareHoldersRelatedThumbnails$: Observable<LiteratureDocument[]>;
  documentDetails$: Observable<LiteratureDocument>;
  paramMap: Map<string, string> = new Map();
  litComponentType: string;
  private unsubscribe$: Subject<void> = new Subject<void>();

  /**
   * Angular device detection
   */
  constructor(
    private responsiveService: ResponsiveService,
    private complimentaryLiteratureService: ComplimentaryLiteratureService,
    private literatureConfig: LiteratureGlobalConfigService
  ) {
    super();
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isMobile$ = this.responsiveService.isMobile$();
  }

  ngOnInit(): void {
    const config = this.component?.getModels()?.config;
    this.litComponentType = config?.litComponentType;
    this.paramMap.set('literatureCode', config?.literatureCode);
    this.paramMap.set('cache', 'true');
    this.documentDetails$ = this.complimentaryLiteratureService.populate(
      this.paramMap,
      true
    );
    this.documentDetails$.pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data) => {
        if (data?.mandatoryMaterial) {
          const ltcodes = data?.mandatoryMaterial?.toString();
          this.mandatoryThumbnails$ = this.getThumbnails(ltcodes);
          this.shareHoldersRelatedThumbnails$ = this.getRelatedDocuments(
            data?.mandatoryMaterial
          )?.pipe(
            map((relatedData) => {
              if (data.litPathPrevious) {
                relatedData.push({
                  ...data,
                  pdfDownloadLink: data.litPathPrevious,
                });
              }
              return relatedData;
            })
          );
        }
      },
      (errorResponse) => {
        if (errorResponse?.status === 404) {
          logger.warn(
            '[populate]' +
              errorResponse?.message +
              ' code: ' +
              errorResponse?.error?.message
          );
        }
      }
    );
    this.titleLinkBase = this.literatureConfig.navigationUrls.litDetailUrl;
  }

  getRelatedDocuments(data: string[]): Observable<LiteratureDocument[]> {
    const relatedDoc = data.filter((item) => {
      const litCodeSubString = item.split(/-/);
      if (litCodeSubString[1] === Q1PH || litCodeSubString[1] === Q3PH) {
        return true;
      } else {
        return false;
      }
    });
    if (relatedDoc.length > 0) {
      return this.getThumbnails(relatedDoc.toString());
    }
  }

  getThumbnails(ltcodes: string): Observable<LiteratureDocument[]> {
    const litCodeParams: Map<string, string> = new Map();
    litCodeParams.set('literatureCode', ltcodes);
    litCodeParams.set('cache', 'true');

    return this.complimentaryLiteratureService.getTumbnailItems(litCodeParams);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
